import { AlertTriangle, CheckCircle2, Radar, Square } from "lucide-react";
import React, { useState } from "react";

import type * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { fmtDateTime, int0, timeAgo } from "../format";
import { OzonModal } from "./OzonModal";
import { SlotWatchJournal } from "./SlotWatchJournal";
import { dropOffBadge } from "./dropOff";
import {
  WATCH_STATUS_LABEL,
  fmtShort,
  sensorMode,
  sensorSlots,
  tickStale,
} from "./slotWatch";
import { fmtSlot } from "./slotFormat";

const STATUS_TONE: Record<ozon.SlotWatchStatus, string> = {
  active: "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300",
  booked: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  stopped: "bg-muted text-muted-foreground",
  expired: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
};

export function WatchStatusBadge({
  status,
}: {
  status: ozon.SlotWatchStatus;
}): React.ReactElement {
  return (
    <span className={cn("rounded px-1.5 py-0.5 text-xs font-medium", STATUS_TONE[status])}>
      {WATCH_STATUS_LABEL[status] ?? status}
    </span>
  );
}

/** Карточка мониторинга мест на странице поставки — активного или последнего.
 *
 *  Отвечает менеджеру с одного взгляда: следит ли наблюдатель сейчас (последний тик; молчит дольше
 *  трёх минут — КРАСНОЙ строкой), что видит датчик пункта, что решил последним и чем споткнулся,
 *  а после брони — номер заявки, поставки и окно.
 */
export function SlotWatchCard({
  w,
  now,
  stopping,
  stopError,
  onAskStop,
  onStop,
}: {
  w: ozon.SlotWatchView;
  now: number;
  stopping: boolean;
  /** отказ снятия — текстом в окне подтверждения */
  stopError: string | null;
  /** открыли подтверждение — прошлый отказ больше не про это нажатие */
  onAskStop: () => void;
  /** снять; `done` — после успеха, закрыть окно */
  onStop: (done: () => void) => void;
}): React.ReactElement {
  const [confirming, setConfirming] = useState(false);
  const active = w.status === "active";
  const stale = tickStale(w, now);
  const badge = dropOffBadge(w.drop_off_type);
  const slots = sensorSlots(w.sensor);
  const b = w.booked;
  // ⚠️ черновик показываем только у активного и пока он жив: у снятого/истёкшего/забронированного
  // поле — остаток последнего тика, и «жив до» в прошлом читалось бы как живой черновик
  const draftAlive =
    active &&
    !!w.draft_id &&
    !!w.draft_alive_until &&
    new Date(w.draft_alive_until).getTime() > now;

  return (
    <section aria-label="Мониторинг мест" className="space-y-2 rounded-md border bg-card px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="flex items-center gap-1.5 font-medium">
          <Radar className={cn("size-4", active && "text-sky-600 dark:text-sky-400")} />
          Мониторинг мест
        </span>
        <WatchStatusBadge status={w.status} />
        <span className="text-xs text-muted-foreground">
          пункт <b className="text-foreground">{w.drop_off_name}</b>
          {badge && ` · ${badge}`}
        </span>
        <span className="text-xs text-muted-foreground">
          окна не раньше чем через <b className="text-foreground">{w.not_before_hours} ч</b>
        </span>
        <span className="text-xs text-muted-foreground" title={fmtDateTime(w.expires_at)}>
          следит до <b className="text-foreground">{fmtShort(new Date(w.expires_at))}</b>
        </span>
        <span className="text-xs text-muted-foreground">
          поставил {w.created_by} · {fmtDateTime(w.created_at)}
        </span>
        {active && (
          <Button
            size="sm"
            variant="outline"
            className="ml-auto"
            disabled={stopping}
            onClick={() => {
              onAskStop();
              setConfirming(true);
            }}
          >
            <Square className="size-4" /> Снять с мониторинга
          </Button>
        )}
      </div>

      {stale && (
        <div
          role="alert"
          className="flex items-center gap-1.5 rounded border border-red-300 bg-red-50 px-3 py-1 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          <AlertTriangle className="size-3.5 shrink-0" /> {stale}
        </div>
      )}

      <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
        {active && (
          <Item label="Последний тик">
            {w.last_tick_at ? (
              <span title={fmtDateTime(w.last_tick_at)}>
                {timeAgo(w.last_tick_at)} · {fmtDateTime(w.last_tick_at)}
              </span>
            ) : (
              <span className="text-muted-foreground">тиков ещё не было</span>
            )}
          </Item>
        )}
        <Item label="Датчик">
          <span>{sensorMode(w.sensor)}</span>
          {w.sensor?.checked_at && (
            <span className="text-muted-foreground" title={fmtDateTime(w.sensor.checked_at)}>
              {" "}
              · проверен {timeAgo(w.sensor.checked_at)}
            </span>
          )}
          {slots && <div className="text-muted-foreground">{slots}</div>}
        </Item>
        {draftAlive && (
          <Item label="Черновик">
            {w.draft_id}
            <span className="text-muted-foreground">
              {" "}
              · жив до {fmtShort(new Date(w.draft_alive_until!))}
            </span>
          </Item>
        )}
        <Item label="Последнее решение">
          {w.last_decision ?? <span className="text-muted-foreground">—</span>}
        </Item>
        {w.last_error && (
          <Item label="Ошибка">
            <span className="text-red-700 dark:text-red-400">{w.last_error}</span>
          </Item>
        )}
      </dl>

      {w.status === "booked" && b && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
          <CheckCircle2 className="size-4 shrink-0" />
          <span>
            Заявка создана: № <b>{b.order_id ?? "—"}</b>
          </span>
          {b.supply_number && (
            <span>
              поставка <b>{b.supply_number}</b>
            </span>
          )}
          {b.slot_from && (
            <span>
              окно <b>{fmtSlot(b.slot_from)}</b>
              {b.slot_to && `–${b.slot_to.slice(11, 16)}`} (время пункта сдачи)
            </span>
          )}
          {b.requested != null && b.accepted != null && (
            <span>
              просили {int0(b.requested)} → взяли{" "}
              <b
                className={cn(
                  b.accepted < b.requested && "text-amber-700 dark:text-amber-400",
                )}
              >
                {int0(b.accepted)}
              </b>
            </span>
          )}
        </div>
      )}
      {w.status === "stopped" && (
        <p className="text-xs text-muted-foreground">
          Снял {w.stopped_by ?? "—"} · {fmtDateTime(w.stopped_at)}
          {w.stop_reason && `: ${w.stop_reason}`}
        </p>
      )}
      {w.status === "expired" && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Срок мониторинга истёк {fmtDateTime(w.expires_at)}
          {w.stop_reason && `: ${w.stop_reason}`}
        </p>
      )}

      <SlotWatchJournal watchId={w.id} live={active} />

      {confirming && (
        <OzonModal title="Снять с мониторинга?" onClose={() => setConfirming(false)}>
          <div className="space-y-3 text-sm">
            <p>
              Наблюдатель перестанет смотреть окна у пункта <b>{w.drop_off_name}</b>, и заявка сама
              не создастся. Ручные действия по поставке (черновик, окна, пункт сдачи) снова станут
              доступны.
            </p>
            <p className="text-xs text-muted-foreground">
              Черновик, снятый наблюдателем, площадка удалит сама примерно через 30 минут.
            </p>
            {stopError && (
              <div
                role="alert"
                className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
              >
                ⚠ {stopError}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirming(false)} disabled={stopping}>
                Оставить
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={stopping}
                onClick={() => onStop(() => setConfirming(false))}
              >
                <Square className={cn("size-4", stopping && "animate-pulse")} /> Снять
              </Button>
            </div>
          </div>
        </OzonModal>
      )}
    </section>
  );
}

function Item({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}
